import { supabase } from '@/lib/supabase';
import type { Database } from '@/types/supabase';
import type { User as SupabaseUser } from '@supabase/supabase-js';
import { cacheManager } from '@/components/cache/CacheManager';

export type User = Database['public']['Tables']['users']['Row'] & {
  auth_user?: SupabaseUser;
  current_account_id?: string;
};
type UserInsert = Database['public']['Tables']['users']['Insert'];
type UserUpdate = Database['public']['Tables']['users']['Update'];

// Tipos auxiliares para contornar problemas de inferência do Supabase
type SafeUserInsert = {
  id: string;
  full_name?: string | null;
  avatar_url?: string | null;
  created_at?: string;
  updated_at?: string;
};

type SafeUserUpdate = {
  id?: string;
  full_name?: string | null;
  avatar_url?: string | null;
  created_at?: string;
  updated_at?: string;
};

export class UserService {
  static async me(): Promise<User | null> {
    const { data: { user: authUser } } = await supabase.auth.getUser();
    if (!authUser) return null;

    console.log('Auth user:', {
      id: authUser.id,
      email: authUser.email,
      metadata: authUser.user_metadata
    });

    // Verificar cache primeiro
    const cacheKey = `user_${authUser.id}`;
    const cachedUser = cacheManager.getUser(authUser.id);
    if (cachedUser) {
      console.log('Returning cached user data');
      return {
        ...cachedUser,
        auth_user: authUser,
        current_account_id: authUser.user_metadata?.current_account_id
      };
    }

    const { data: profile, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', authUser.id)
      .single();

    console.log('User profile:', { profile, error });

    // Se o perfil não existe, vamos criá-lo
    if (error?.code === 'PGRST116' || !profile) {
      console.log('Creating new user profile...');
      const newUserData: SafeUserInsert = {
        id: authUser.id,
        full_name: authUser.user_metadata?.full_name || authUser.user_metadata?.name || authUser.email?.split('@')[0] || null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      const { data: newProfile, error: createError } = await supabase
        .from('users')
        .insert(newUserData as any)
        .select()
        .single();

      console.log('Created profile:', { newProfile, createError });

      if (createError) throw createError;
      if (!newProfile) throw new Error('Failed to create user profile');

      if (!newProfile) throw new Error('Failed to create user profile');
      
      return {
        ...(newProfile as Database['public']['Tables']['users']['Row']),
        auth_user: authUser,
        current_account_id: authUser.user_metadata?.current_account_id
      };
    }

    if (error) throw error;

    if (!profile) throw new Error('User profile not found');
    
    // Cache os dados do usuário
    cacheManager.cacheUser(authUser.id, profile as Database['public']['Tables']['users']['Row']);
    
    return {
      ...(profile as Database['public']['Tables']['users']['Row']),
      auth_user: authUser,
      current_account_id: authUser.user_metadata?.current_account_id
    };
  }


  static async update(id: string, data: SafeUserUpdate): Promise<User> {
    const updateData: SafeUserUpdate = {
      ...data,
      updated_at: new Date().toISOString()
    };
    
    const { data: updatedUser, error } = await (supabase as any)
      .from('users')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    if (!updatedUser) throw new Error('Failed to update user');

    return updatedUser as User;
  }

  static async updateAvatar(id: string, avatarUrl: string): Promise<User> {
    return this.update(id, { avatar_url: avatarUrl });
  }

  static async updateMyUserData(data: SafeUserUpdate): Promise<User> {
    const { data: { user: authUser } } = await supabase.auth.getUser();
    if (!authUser) throw new Error('User not authenticated');
    
    const updatedUser = await this.update(authUser.id, data);
    
    // Invalidar cache do usuário para forçar recarregamento
    cacheManager.invalidatePattern(`user_${authUser.id}`);
    cacheManager.invalidatePattern(`user_photo_${authUser.id}`);
    
    return updatedUser;
  }

}

// Re-export para manter compatibilidade com código existente
export const User = UserService;
